import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown, ChevronRight, ArrowLeft, BarChart3, AlertTriangle, Clock, Calendar, Users, Sparkles, LayoutDashboard, Building2, Briefcase, Menu, X, FileText, Zap, Droplets, Paintbrush, Home, Building } from "lucide-react";
import { useState, useEffect } from "react";

const productItems = [
  {
    icon: BarChart3,
    title: "Rentabilité temps réel",
    description: "Suivez vos marges en continu",
    gradient: "from-[#10B981] to-[#059669]",
    href: "/product/rentabilite",
  },
  {
    icon: AlertTriangle,
    title: "Jour Critique",
    description: "Anticipez les dépassements",
    gradient: "from-[#EF4444] to-[#DC2626]",
    href: "/product/jour-critique",
  },
  {
    icon: Clock,
    title: "Simulation retards",
    description: "Impact financier des délais",
    gradient: "from-[#F59E0B] to-[#D97706]",
    href: "/product/retards",
  },
  {
    icon: Calendar,
    title: "Planning & Rotations",
    description: "Gérez vos affectations",
    gradient: "from-[#06B6D4] to-[#0891B2]",
    href: "/product/planning",
  },
  {
    icon: Users,
    title: "Gestion équipes",
    description: "Coûts main d'œuvre auto",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
    href: "/product/equipes",
  },
  {
    icon: Sparkles,
    title: "Analyse IA",
    description: "Recommandations intelligentes",
    gradient: "from-[#EC4899] to-[#DB2777]",
    href: "/product/analyse-ia",
  },
  {
    icon: LayoutDashboard,
    title: "Centre de pilotage",
    description: "Vue d'ensemble complète",
    gradient: "from-[#3B82F6] to-[#2563EB]",
    href: "/product/centre-pilotage",
  },
  {
    icon: FileText,
    title: "Rapports & Documents",
    description: "Suivi & clôture imprimables",
    gradient: "from-[#14B8A6] to-[#0D9488]",
    href: "/product/rapports",
  },
];

const useCaseItems = [
  {
    icon: Building2,
    title: "Par métier",
    items: [
      { label: "Électricien", href: "/use-cases/electricien", icon: Zap, gradient: "from-[#FBBF24] to-[#F59E0B]" },
      { label: "Plombier-Chauffagiste", href: "/use-cases/plombier", icon: Droplets, gradient: "from-[#06B6D4] to-[#0891B2]" },
      { label: "Maçon / Entreprise générale", href: "/use-cases/macon", icon: Building2, gradient: "from-[#EF4444] to-[#DC2626]" },
      { label: "Peintre", href: "/use-cases/peintre", icon: Paintbrush, gradient: "from-[#8B5CF6] to-[#7C3AED]" },
      { label: "Couvreur-Charpentier", href: "/use-cases/couvreur", icon: Home, gradient: "from-[#10B981] to-[#059669]" },
      { label: "Entreprise tous corps d'état", href: "/use-cases/multi-corps", icon: Building, gradient: "from-[#3B82F6] to-[#2563EB]" },
    ],
  },
];

const LandingHeader = () => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuLevel, setMenuLevel] = useState<'main' | 'produit' | 'usecases'>('main');
  const [scrolled, setScrolled] = useState(false);

  // Détecter le scroll pour réduire la largeur du menu
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset menu level when menu closes
  useEffect(() => {
    if (!mobileMenuOpen) {
      // Small delay to allow close animation to complete
      const timer = setTimeout(() => setMenuLevel('main'), 300);
      return () => clearTimeout(timer);
    }
  }, [mobileMenuOpen]);

  // Handler pour fermer le menu et naviguer
  const handleMobileNavigation = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full">
      <div className="relative mx-auto px-4 sm:px-6">
        <div className={`relative flex h-16 items-center justify-between border-b border-white/[0.06] bg-[#0A0A0F]/80 backdrop-blur-xl rounded-[20px] mt-2 px-6 transition-all duration-700 ease-in-out ${scrolled ? "mx-auto max-w-3xl" : "mx-4"}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <span className="text-2xl font-bold text-white tracking-tight">
              B<span className="text-[#F59E0B]">8</span>ild
            </span>
          </Link>

          {/* Navigation - Desktop */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Produit Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("produit")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to="/product"
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]"
              >
                Produit
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === "produit" ? "rotate-180" : ""}`} />
              </Link>

              {/* Produit Mega Menu */}
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${activeDropdown === "produit" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                <div className="w-[480px] rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent shadow-2xl">
                  <div className="rounded-2xl bg-[#0F0F14]/95 backdrop-blur-xl p-4">
                    <div className="grid grid-cols-2 gap-2">
                      {productItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={index}
                            to={item.href}
                            className="group flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.05] transition-colors"
                          >
                            <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white group-hover:text-[#F59E0B] transition-colors">
                                {item.title}
                              </p>
                              <p className="text-xs text-white/40 mt-0.5">
                                {item.description}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    {/* Lien vers toutes les fonctionnalités */}
                    <div className="mt-4 pt-4 border-t border-white/[0.05]">
                      <Link
                        to="/product"
                        className="flex items-center justify-center gap-2 text-sm text-white/60 hover:text-[#F59E0B] transition-colors"
                      >
                        Voir toutes les fonctionnalités
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tarifs */}
            <Link
              to="/pricing"
              className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]"
            >
              Tarifs
            </Link>

            {/* Blog */}
            <Link
              to="/blog"
              className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]"
            >
              Blog
            </Link>

            {/* Cas d'usage Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("usecases")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to="/use-cases"
                className={`flex items-center gap-1.5 px-4 py-2 font-medium text-white/70 hover:text-white transition-all rounded-lg hover:bg-white/[0.05] whitespace-nowrap ${scrolled ? "text-xs" : "text-sm"}`}
              >
                {scrolled ? "Use Cases" : "Cas d'usage"}
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === "usecases" ? "rotate-180" : ""}`} />
              </Link>

              {/* Cas d'usage Dropdown */}
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${activeDropdown === "usecases" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                <div className="w-[320px] rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent shadow-2xl">
                  <div className="rounded-2xl bg-[#0F0F14]/95 backdrop-blur-xl p-4">
                    <div className="space-y-4">
                      {useCaseItems.map((section, index) => {
                        const Icon = section.icon;
                        return (
                          <div key={index}>
                            <div className="flex items-center gap-2 mb-2 px-2">
                              <Icon className="h-4 w-4 text-[#F59E0B]" />
                              <span className="text-xs font-medium text-white/50 uppercase tracking-wider">
                                {section.title}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {section.items.map((item, i) => (
                                <Link
                                  key={i}
                                  to={item.href}
                                  className="block px-3 py-2 text-sm text-white/70 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors"
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Lien vers tous les cas d'usage */}
                    <div className="mt-4 pt-4 border-t border-white/[0.05]">
                      <Link
                        to="/use-cases"
                        className="flex items-center justify-center gap-2 text-sm text-white/60 hover:text-[#F59E0B] transition-colors"
                      >
                        Voir tous les cas d'usage
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-white/60 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:flex text-white/60 hover:text-white hover:bg-white/[0.05] font-medium h-9 px-4 rounded-lg transition-all duration-200"
              asChild
            >
              <Link to="/auth">Connexion</Link>
            </Button>
            <Button
              size="sm"
              className="h-9 px-4 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold gap-2 rounded-lg shadow-[0_2px_10px_rgba(245,158,11,0.3)] hover:shadow-[0_4px_20px_rgba(245,158,11,0.4)] transition-all duration-200"
              asChild
            >
              <Link to="/auth?mode=signup">
                <span className="hidden sm:inline">Essayer gratuitement</span>
                <span className="sm:hidden">Essayer</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Mobile Menu - Slide-in Multi-Panneaux - OUTSIDE header bar for proper positioning */}
        <div className={`lg:hidden absolute top-[76px] left-4 right-4 z-[60] rounded-[20px] p-[1px] bg-gradient-to-b from-white/[0.1] to-transparent shadow-2xl transition-all duration-300 ${mobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none"}`}>
          <div className="rounded-[20px] bg-[#0F0F14]/95 backdrop-blur-xl overflow-hidden">
            {/* Container des panneaux avec overflow hidden pour le slide */}
            <div className="relative" style={{ minHeight: menuLevel === 'main' ? 'auto' : '400px' }}>

              {/* Panneau Principal (Niveau 1) */}
              <div className={`transition-all duration-300 ease-in-out ${
                menuLevel === 'main'
                  ? 'translate-x-0 opacity-100'
                  : '-translate-x-full opacity-0 absolute inset-0'
              }`}>
                <div className="p-4 space-y-1">
                  {/* Items avec sous-menu */}
                  <button
                    onClick={() => setMenuLevel('produit')}
                    className="flex items-center justify-between w-full p-3 rounded-xl hover:bg-white/[0.05] transition-colors text-left"
                  >
                    <span className="text-white font-medium">Produit</span>
                    <ChevronRight className="h-5 w-5 text-white/40" />
                  </button>

                  <button
                    onClick={() => setMenuLevel('usecases')}
                    className="flex items-center justify-between w-full p-3 rounded-xl hover:bg-white/[0.05] transition-colors text-left"
                  >
                    <span className="text-white font-medium">Cas d'usage</span>
                    <ChevronRight className="h-5 w-5 text-white/40" />
                  </button>

                  {/* Liens directs */}
                  <Link
                    to="/pricing"
                    className="flex items-center w-full p-3 rounded-xl hover:bg-white/[0.05] transition-colors text-white/70 hover:text-white"
                    onClick={handleMobileNavigation}
                  >
                    Tarifs
                  </Link>
                  <Link
                    to="/blog"
                    className="flex items-center w-full p-3 rounded-xl hover:bg-white/[0.05] transition-colors text-white/70 hover:text-white"
                    onClick={handleMobileNavigation}
                  >
                    Blog
                  </Link>
                </div>

                {/* CTA en bas */}
                <div className="p-4 border-t border-white/[0.05] space-y-2">
                  <Link
                    to="/auth"
                    className="block w-full text-center py-2.5 px-4 rounded-xl border border-white/10 text-white/80 hover:bg-white/[0.05] transition-colors font-medium"
                    onClick={handleMobileNavigation}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/auth?mode=signup"
                    className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold transition-colors"
                    onClick={handleMobileNavigation}
                  >
                    Essayer gratuitement
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              {/* Panneau Produit (Niveau 2) */}
              <div className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                menuLevel === 'produit'
                  ? 'translate-x-0 opacity-100'
                  : 'translate-x-full opacity-0 pointer-events-none'
              }`}>
                <div className="flex flex-col h-full">
                  {/* Header avec bouton retour */}
                  <button
                    onClick={() => setMenuLevel('main')}
                    className="flex items-center gap-2 p-4 border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5 text-white/60" />
                    <span className="text-white font-medium">Produit</span>
                  </button>

                  {/* Liste des produits */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-1">
                    {productItems.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={index}
                          to={item.href}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.05] transition-colors"
                          onClick={handleMobileNavigation}
                        >
                          <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{item.title}</p>
                            <p className="text-xs text-white/40">{item.description}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="p-4 border-t border-white/[0.05]">
                    <Link
                      to="/product"
                      className="flex items-center justify-center gap-2 text-sm text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
                      onClick={handleMobileNavigation}
                    >
                      Voir toutes les fonctionnalités
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Panneau Cas d'usage (Niveau 2) */}
              <div className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                menuLevel === 'usecases'
                  ? 'translate-x-0 opacity-100'
                  : 'translate-x-full opacity-0 pointer-events-none'
              }`}>
                <div className="flex flex-col h-full">
                  {/* Header avec bouton retour */}
                  <button
                    onClick={() => setMenuLevel('main')}
                    className="flex items-center gap-2 p-4 border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5 text-white/60" />
                    <span className="text-white font-medium">Cas d'usage</span>
                  </button>

                  {/* Liste des métiers */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-1">
                    <p className="text-xs font-medium text-white/50 uppercase tracking-wider mb-3 px-3">Par métier</p>
                    {useCaseItems[0].items.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={index}
                          to={item.href}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.05] transition-colors"
                          onClick={handleMobileNavigation}
                        >
                          <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <span className="text-white font-medium text-sm">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="p-4 border-t border-white/[0.05]">
                    <Link
                      to="/use-cases"
                      className="flex items-center justify-center gap-2 text-sm text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
                      onClick={handleMobileNavigation}
                    >
                      Voir tous les cas d'usage
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default LandingHeader;
